import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import {
  Loader2, ArrowLeft, ArrowRight, Check, Image as ImageIcon,
  User, Phone, Clock, Share2, Palette, Sparkles, Globe, Mail, MessageCircle, MapPin,
  Instagram, Twitter, Facebook, Youtube,
} from 'lucide-react';
import { websiteAPI } from '../../services/api';
import { useTenantBranding } from '../../context/TenantBrandingContext';
import MediaPicker from '../../components/media/MediaPicker';

/**
 * Five-step wizard that runs immediately after a template is selected. It
 * collects the minimum fields a lab needs to publish a real-looking site,
 * then fires `/website/complete-onboarding` which seeds SitePage overrides
 * and flips `publicSite.enabled=true`.
 *
 * Fields map 1:1 onto manifest keys:
 *   step 1 → site.logo, site.labelName
 *   step 2 → contact.{phone, whatsapp, email, address}
 *   step 3 → footer.workingHours
 *   step 4 → social.{instagram, twitter, facebook, snapchat, tiktok, youtube, whatsapp}
 *            + theme.primaryColor
 *   step 5 → review + submit
 */

const STEPS = [
  { key: 'brand',    title: 'الهوية البصرية',    subtitle: 'شعار المختبر واسمه', icon: User },
  { key: 'contact',  title: 'معلومات التواصل',   subtitle: 'هاتف، واتساب، إيميل، عنوان', icon: Phone },
  { key: 'hours',    title: 'ساعات العمل',       subtitle: 'متى يستقبل المختبر الزوار', icon: Clock },
  { key: 'social',   title: 'التواصل واللون',    subtitle: 'روابط اجتماعية + اللون الأساسي', icon: Share2 },
  { key: 'review',   title: 'المراجعة',          subtitle: 'نظرة أخيرة قبل الإنشاء', icon: Check },
];

const EMPTY = {
  logoUrl: '',
  labelName: { ar: '', en: '' },
  contact: { phone: '', whatsapp: '', email: '', address: { ar: '', en: '' } },
  workingHours: { ar: '', en: '' },
  social: { instagram: '', twitter: '', facebook: '', snapchat: '', tiktok: '', youtube: '', whatsapp: '' },
  primaryColor: '#2563EB',
};

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-semibold text-slate-700 mb-1.5">{label}</span>
      {children}
      {hint && <span className="block text-[11px] text-slate-400 mt-1">{hint}</span>}
    </label>
  );
}

function TextInput({ value, onChange, placeholder, type = 'text', dir, icon: Icon }) {
  return (
    <div className="relative">
      {Icon && (
        <Icon className="absolute top-1/2 -translate-y-1/2 end-3 w-4 h-4 text-slate-400 pointer-events-none" />
      )}
      <input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        dir={dir}
        className={`w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors ${Icon ? 'pe-9' : ''}`}
      />
    </div>
  );
}

function LocaleInput({ valueAr, valueEn, onChangeAr, onChangeEn, placeholderAr, placeholderEn, type = 'text' }) {
  const [tab, setTab] = useState('ar');
  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden">
      <div className="flex border-b border-slate-200 bg-slate-50">
        <button type="button" onClick={() => setTab('ar')}
          className={`flex-1 px-3 py-2 text-xs font-semibold ${tab === 'ar' ? 'bg-white text-blue-600' : 'text-slate-500'}`}>
          العربية
        </button>
        <button type="button" onClick={() => setTab('en')}
          className={`flex-1 px-3 py-2 text-xs font-semibold ${tab === 'en' ? 'bg-white text-blue-600' : 'text-slate-500'}`}>
          English
        </button>
      </div>
      <div className="p-2">
        {tab === 'ar' ? (
          type === 'textarea' ? (
            <textarea value={valueAr || ''} onChange={(e) => onChangeAr(e.target.value)} placeholder={placeholderAr} dir="rtl" rows={3}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 resize-none" />
          ) : (
            <input type="text" value={valueAr || ''} onChange={(e) => onChangeAr(e.target.value)} placeholder={placeholderAr} dir="rtl"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" />
          )
        ) : (
          type === 'textarea' ? (
            <textarea value={valueEn || ''} onChange={(e) => onChangeEn(e.target.value)} placeholder={placeholderEn} dir="ltr" rows={3}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 resize-none" />
          ) : (
            <input type="text" value={valueEn || ''} onChange={(e) => onChangeEn(e.target.value)} placeholder={placeholderEn} dir="ltr"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" />
          )
        )}
      </div>
    </div>
  );
}

const WebsiteOnboardingWizard = () => {
  const navigate = useNavigate();
  const { tenant, fetchTenant } = useTenantBranding();

  const [stepIdx, setStepIdx] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Pre-fill from tenant.branding + tenant.nameAr/name (English fallback).
  useEffect(() => {
    if (!tenant) return;
    setForm((prev) => ({
      ...prev,
      logoUrl: prev.logoUrl || tenant.branding?.logoUrl || '',
      labelName: {
        ar: prev.labelName.ar || tenant.nameAr || '',
        en: prev.labelName.en || tenant.name || '',
      },
      primaryColor: prev.primaryColor && prev.primaryColor !== EMPTY.primaryColor
        ? prev.primaryColor
        : (tenant.branding?.primaryColor || EMPTY.primaryColor),
    }));
  }, [tenant]);

  const step = STEPS[stepIdx];
  const progress = Math.round(((stepIdx + 1) / STEPS.length) * 100);

  const canAdvance = useMemo(() => {
    if (step.key === 'brand') {
      return !!(form.labelName.ar || form.labelName.en);
    }
    // Other steps are all optional — we let users skip social/hours if they
    // don't have them yet. Review step has its own gate (submit).
    return true;
  }, [step.key, form.labelName]);

  const goNext = () => setStepIdx((i) => Math.min(i + 1, STEPS.length - 1));
  const goPrev = () => setStepIdx((i) => Math.max(i - 1, 0));

  const onSubmit = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await websiteAPI.completeOnboarding(form);
      toast.success('تم إنشاء موقعك بنجاح 🎉');
      await fetchTenant();
      // Give toast a beat, then land on the builder.
      setTimeout(() => navigate('/settings/website'), 900);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'تعذّر إكمال الإعداد');
      setSubmitting(false);
    }
  }, [form, navigate, fetchTenant, submitting]);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" dir="rtl">
      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center shadow-sm shrink-0">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-extrabold text-slate-900 mb-1">إعداد موقعك الإلكتروني</h1>
              <p className="text-slate-500 text-sm">بضع خطوات سريعة وسيكون موقعك جاهزاً.</p>
            </div>
          </div>
        </motion.div>

        {/* Stepper */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm mb-6 p-5">
          <div className="flex items-center gap-2 mb-4">
            {STEPS.map((s, i) => (
              <React.Fragment key={s.key}>
                <div className={`flex items-center gap-2 flex-shrink-0 ${i === stepIdx ? 'text-blue-600' : i < stepIdx ? 'text-emerald-600' : 'text-slate-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                    i === stepIdx ? 'bg-blue-100 text-blue-700' :
                    i < stepIdx ? 'bg-emerald-100 text-emerald-700' :
                    'bg-slate-100 text-slate-400'
                  }`}>
                    {i < stepIdx ? <Check className="w-4 h-4" /> : i + 1}
                  </div>
                  <span className="hidden sm:inline text-xs font-semibold">{s.title}</span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 rounded-full ${i < stepIdx ? 'bg-emerald-400' : 'bg-slate-200'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
          <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
            <motion.div className="h-full bg-blue-600" animate={{ width: `${progress}%` }} transition={{ type: 'spring', stiffness: 120, damping: 20 }} />
          </div>
        </div>

        {/* Step body */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
          <div className="p-5 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                <step.icon className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-900">{step.title}</h2>
                <p className="text-xs text-slate-500">{step.subtitle}</p>
              </div>
            </div>
          </div>

          <div className="p-6">
            <AnimatePresence mode="wait">
              <motion.div key={step.key} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>
                {step.key === 'brand' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Field label="شعار المختبر" hint="يظهر في الهيدر والفوتر. تجاوز هذه الخطوة لاستخدام شعار LabTechs افتراضياً.">
                      <div className="flex items-center gap-3">
                        {form.logoUrl ? (
                          <img src={form.logoUrl} alt="logo" className="w-20 h-20 rounded-xl border border-slate-200 object-contain bg-white p-2" />
                        ) : (
                          <div className="w-20 h-20 rounded-xl border-2 border-dashed border-slate-200 flex items-center justify-center text-slate-300">
                            <ImageIcon className="w-7 h-7" />
                          </div>
                        )}
                        <div className="flex flex-col gap-2">
                          <button type="button" onClick={() => setPickerOpen(true)}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700">
                            <ImageIcon className="w-3.5 h-3.5" />
                            {form.logoUrl ? 'تغيير' : 'رفع شعار'}
                          </button>
                          {form.logoUrl && (
                            <button type="button" onClick={() => setForm((f) => ({ ...f, logoUrl: '' }))}
                              className="text-xs text-slate-500 hover:text-blue-600">إزالة الشعار</button>
                          )}
                        </div>
                      </div>
                    </Field>
                    <Field label="اسم المختبر (سيظهر في الهيدر والميتا)">
                      <LocaleInput
                        valueAr={form.labelName.ar}
                        valueEn={form.labelName.en}
                        onChangeAr={(v) => setForm((f) => ({ ...f, labelName: { ...f.labelName, ar: v } }))}
                        onChangeEn={(v) => setForm((f) => ({ ...f, labelName: { ...f.labelName, en: v } }))}
                        placeholderAr="مثال: مختبرات السلام الطبية"
                        placeholderEn="e.g. Alsalam Medical Labs"
                      />
                    </Field>
                  </div>
                )}

                {step.key === 'contact' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Field label="رقم الهاتف">
                      <TextInput value={form.contact.phone} onChange={(v) => setForm((f) => ({ ...f, contact: { ...f.contact, phone: v } }))} placeholder="920012345" dir="ltr" icon={Phone} />
                    </Field>
                    <Field label="واتساب">
                      <TextInput value={form.contact.whatsapp} onChange={(v) => setForm((f) => ({ ...f, contact: { ...f.contact, whatsapp: v } }))} placeholder="966500000000" dir="ltr" icon={MessageCircle} />
                    </Field>
                    <Field label="البريد الإلكتروني">
                      <TextInput type="email" value={form.contact.email} onChange={(v) => setForm((f) => ({ ...f, contact: { ...f.contact, email: v } }))} placeholder="info@example.com" dir="ltr" icon={Mail} />
                    </Field>
                    <Field label="العنوان">
                      <LocaleInput
                        valueAr={form.contact.address.ar}
                        valueEn={form.contact.address.en}
                        onChangeAr={(v) => setForm((f) => ({ ...f, contact: { ...f.contact, address: { ...f.contact.address, ar: v } } }))}
                        onChangeEn={(v) => setForm((f) => ({ ...f, contact: { ...f.contact, address: { ...f.contact.address, en: v } } }))}
                        placeholderAr="الرياض، حي النخيل"
                        placeholderEn="Riyadh, Al-Nakheel"
                      />
                    </Field>
                  </div>
                )}

                {step.key === 'hours' && (
                  <div className="max-w-xl">
                    <Field label="ساعات العمل" hint="مثال: السبت - الخميس: 8 صباحاً - 10 مساءً">
                      <LocaleInput
                        valueAr={form.workingHours.ar}
                        valueEn={form.workingHours.en}
                        onChangeAr={(v) => setForm((f) => ({ ...f, workingHours: { ...f.workingHours, ar: v } }))}
                        onChangeEn={(v) => setForm((f) => ({ ...f, workingHours: { ...f.workingHours, en: v } }))}
                        placeholderAr="السبت - الخميس: 8 صباحاً - 10 مساءً"
                        placeholderEn="Sat - Thu: 8 AM - 10 PM"
                      />
                    </Field>
                  </div>
                )}

                {step.key === 'social' && (
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                    <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                      {[
                        { k: 'instagram', label: 'Instagram', icon: Instagram, ph: 'https://instagram.com/labname' },
                        { k: 'twitter',   label: 'Twitter / X', icon: Twitter, ph: 'https://x.com/labname' },
                        { k: 'facebook',  label: 'Facebook', icon: Facebook, ph: 'https://facebook.com/labname' },
                        { k: 'snapchat',  label: 'Snapchat', icon: Globe, ph: 'https://snapchat.com/add/labname' },
                        { k: 'tiktok',    label: 'TikTok', icon: Globe, ph: 'https://tiktok.com/@labname' },
                        { k: 'youtube',   label: 'YouTube', icon: Youtube, ph: 'https://youtube.com/@labname' },
                      ].map((s) => (
                        <Field key={s.k} label={s.label}>
                          <TextInput
                            value={form.social[s.k]}
                            onChange={(v) => setForm((f) => ({ ...f, social: { ...f.social, [s.k]: v } }))}
                            placeholder={s.ph} dir="ltr" icon={s.icon}
                          />
                        </Field>
                      ))}
                    </div>
                    <div>
                      <Field label="اللون الأساسي" hint="يحدد لون الأزرار والعناصر البارزة في موقعك.">
                        <div className="flex items-center gap-3">
                          <input
                            type="color"
                            value={form.primaryColor || '#2563EB'}
                            onChange={(e) => setForm((f) => ({ ...f, primaryColor: e.target.value }))}
                            className="w-14 h-14 rounded-xl border border-slate-200 cursor-pointer"
                          />
                          <input
                            type="text"
                            value={form.primaryColor || ''}
                            onChange={(e) => setForm((f) => ({ ...f, primaryColor: e.target.value }))}
                            dir="ltr"
                            className="flex-1 px-3 py-2.5 border border-slate-200 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                          />
                        </div>
                      </Field>
                      <div className="mt-3 p-3 rounded-xl border border-slate-200 bg-slate-50">
                        <div className="text-[11px] text-slate-500 mb-2">معاينة</div>
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-10 rounded-xl shadow-sm" style={{ background: form.primaryColor }} />
                          <button type="button" className="px-3 py-2 rounded-lg text-white text-xs font-semibold" style={{ background: form.primaryColor }}>
                            زر تجريبي
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {step.key === 'review' && (
                  <div className="space-y-4">
                    <ReviewRow icon={ImageIcon} label="الشعار">
                      {form.logoUrl
                        ? <img src={form.logoUrl} alt="logo" className="w-12 h-12 rounded-lg object-contain bg-white border border-slate-200 p-1" />
                        : <span className="text-xs text-slate-400">سيستخدم شعار LabTechs الافتراضي</span>}
                    </ReviewRow>
                    <ReviewRow icon={User} label="الاسم">
                      <span className="text-sm text-slate-700">{form.labelName.ar || '—'}</span>
                      {form.labelName.en && <span className="text-xs text-slate-400 mx-2">/ {form.labelName.en}</span>}
                    </ReviewRow>
                    <ReviewRow icon={Phone} label="التواصل">
                      <span className="text-xs text-slate-600" dir="ltr">
                        {[form.contact.phone, form.contact.whatsapp, form.contact.email].filter(Boolean).join(' · ') || '—'}
                      </span>
                    </ReviewRow>
                    <ReviewRow icon={MapPin} label="العنوان">
                      <span className="text-sm text-slate-700">{form.contact.address.ar || form.contact.address.en || '—'}</span>
                    </ReviewRow>
                    <ReviewRow icon={Clock} label="ساعات العمل">
                      <span className="text-sm text-slate-700">{form.workingHours.ar || form.workingHours.en || '—'}</span>
                    </ReviewRow>
                    <ReviewRow icon={Share2} label="السوشال">
                      <span className="text-xs text-slate-600">
                        {Object.values(form.social).filter(Boolean).length} رابط
                      </span>
                    </ReviewRow>
                    <ReviewRow icon={Palette} label="اللون الأساسي">
                      <div className="flex items-center gap-2">
                        <span className="w-5 h-5 rounded-md border border-slate-200" style={{ background: form.primaryColor }} />
                        <span className="text-xs font-mono text-slate-500" dir="ltr">{form.primaryColor}</span>
                      </div>
                    </ReviewRow>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Footer nav */}
          <div className="px-5 py-4 border-t border-slate-100 bg-slate-50/50 rounded-b-2xl flex items-center justify-between">
            <button
              type="button"
              onClick={stepIdx === 0 ? () => navigate('/settings/website?force=gallery') : goPrev}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold text-slate-700 hover:bg-slate-100"
            >
              <ArrowRight className="w-4 h-4" />
              {stepIdx === 0 ? 'رجوع للقوالب' : 'السابق'}
            </button>

            {stepIdx < STEPS.length - 1 ? (
              <button
                type="button"
                onClick={goNext}
                disabled={!canAdvance}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                التالي
                <ArrowLeft className="w-4 h-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={onSubmit}
                disabled={submitting}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-60"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4" /> إنشاء موقعي</>}
              </button>
            )}
          </div>
        </div>
      </div>

      <MediaPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={(asset) => {
          setForm((f) => ({ ...f, logoUrl: asset?.publicUrl || '' }));
          setPickerOpen(false);
        }}
        folder="logos"
      />
    </div>
  );
};

function ReviewRow({ icon: Icon, label, children }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl border border-slate-100 bg-slate-50/50">
      <div className="w-9 h-9 rounded-lg bg-white border border-slate-200 text-blue-600 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] text-slate-400 mb-0.5">{label}</div>
        <div className="flex items-center flex-wrap gap-1">{children}</div>
      </div>
    </div>
  );
}

export default WebsiteOnboardingWizard;
